import React, { useState, useEffect, useRef } from 'react';
import { getBlogs, addBlog, updateBlog, deleteBlog } from '../services/blogService';

export default function BlogManagement() {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingBlog, setEditingBlog] = useState(null); // null means list view, {} means new post, {id, ...} means edit
  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [content, setContent] = useState('');
  const [featuredImage, setFeaturedImage] = useState('');
  const [author, setAuthor] = useState('Admin');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  
  const textareaRef = useRef(null);

  useEffect(() => {
    fetchBlogs();
  }, []);

  // Auto-generate slug from title
  useEffect(() => {
    if (editingBlog && !editingBlog.id) {
      const generatedSlug = title
        .toLowerCase()
        .replace(/[^a-z0-9\s]/g, '')
        .replace(/\s+/g, '-');
      setSlug(generatedSlug);
    }
  }, [title, editingBlog]);

  const fetchBlogs = async () => {
    setLoading(true);
    const data = await getBlogs();
    setBlogs(data);
    setLoading(false);
  };

  const handleCreateNew = () => {
    setEditingBlog({});
    setTitle('');
    setSlug('');
    setContent('');
    setFeaturedImage('');
    setAuthor('Admin');
    setMessage({ type: '', text: '' });
  };

  const handleEdit = (blog) => {
    setEditingBlog(blog);
    setTitle(blog.title);
    setSlug(blog.slug || '');
    setContent(blog.content);
    setFeaturedImage(blog.featuredImage || '');
    setAuthor(blog.author || 'Admin');
    setMessage({ type: '', text: '' });
  };

  const handleCancel = () => {
    setEditingBlog(null);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!title || !content || !slug) {
      setMessage({ type: 'error', text: 'Please fill in title, slug, and content.' });
      return;
    }

    setSaving(true);
    const blogData = { title, slug, content, featuredImage, author };
    
    let result;
    if (editingBlog.id) {
      result = await updateBlog(editingBlog.id, blogData);
    } else {
      result = await addBlog(blogData);
    }

    if (result) {
      setMessage({ type: 'success', text: `Blog post ${editingBlog.id ? 'updated' : 'created'} successfully!` });
      await fetchBlogs();
      setTimeout(() => setEditingBlog(null), 1500);
    } else {
      setMessage({ type: 'error', text: 'Failed to save blog post.' });
    }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this blog post?')) {
      const success = await deleteBlog(id);
      if (success) {
        await fetchBlogs();
      } else {
        alert('Failed to delete blog post.');
      }
    }
  };

  const insertTag = (tag) => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const text = textarea.value;
    const before = text.substring(0, start);
    const selected = text.substring(start, end);
    const after = text.substring(end);

    let newText;
    if (tag === 'b') newText = `${before}<b>${selected}</b>${after}`;
    else if (tag === 'i') newText = `${before}<i>${selected}</i>${after}`;
    else if (tag === 'u') newText = `${before}<u>${selected}</u>${after}`;
    else if (tag === 'h3') newText = `${before}<h3>${selected}</h3>${after}`;
    else if (tag === 'p') newText = `${before}<p>${selected}</p>${after}`;
    else if (tag === 'br') newText = `${before}${selected}<br/>${after}`;
    else if (tag === 'img') {
      const url = prompt('Enter image URL:');
      if (url) newText = `${before}<img src="${url}" alt="image" className="w-full rounded-xl my-4" />${after}`;
      else return;
    }
    else if (tag === 'a') {
      const url = prompt('Enter link URL:');
      if (url) newText = `${before}<a href="${url}" className="text-blue-500 hover:underline" target="_blank">${selected || 'link'}</a>${after}`;
      else return;
    }

    setContent(newText);
    textarea.focus();
  };

  if (loading && !editingBlog) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">Blog Management</h1>
          <p className="text-slate-400 text-lg">Create and manage your website's blog posts.</p>
        </div>
        {!editingBlog && (
          <button
            onClick={handleCreateNew}
            className="px-6 py-3 bg-blue-600 hover:bg-blue-500 text-white rounded-xl font-bold transition-all shadow-lg hover:shadow-blue-500/20 flex items-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Create New Post
          </button>
        )}
      </div>

      {editingBlog ? (
        <form onSubmit={handleSave} className="space-y-6">
          <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 md:p-8 space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <label className="text-sm font-semibold text-slate-300 uppercase tracking-wider">Post Title</label>
                <input
                  type="text"
                  className="w-full px-4 py-3 rounded-xl bg-slate-950 border border-slate-800 text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all"
                  placeholder="Enter post title..."
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  required
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-semibold text-slate-300 uppercase tracking-wider">Post Slug (URL)</label>
                <input
                  type="text"
                  className="w-full px-4 py-3 rounded-xl bg-slate-950 border border-slate-800 text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all"
                  placeholder="my-post-slug"
                  value={slug}
                  onChange={(e) => setSlug(e.target.value)}
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <label className="text-sm font-semibold text-slate-300 uppercase tracking-wider">Featured Image URL</label>
                <input
                  type="text"
                  className="w-full px-4 py-3 rounded-xl bg-slate-950 border border-slate-800 text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all"
                  placeholder="https://images.unsplash.com/..."
                  value={featuredImage}
                  onChange={(e) => setFeaturedImage(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-semibold text-slate-300 uppercase tracking-wider">Author</label>
                <input
                  type="text"
                  className="w-full px-4 py-3 rounded-xl bg-slate-950 border border-slate-800 text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all"
                  placeholder="Admin"
                  value={author}
                  onChange={(e) => setAuthor(e.target.value)}
                />
              </div>
            </div>

            <div className="space-y-4">
              <div className="flex flex-wrap items-center gap-2 p-2 bg-slate-950 border border-slate-800 rounded-t-xl border-b-0">
                <button type="button" onClick={() => insertTag('b')} className="p-2 hover:bg-slate-800 rounded text-white font-bold px-3" title="Bold">B</button>
                <button type="button" onClick={() => insertTag('i')} className="p-2 hover:bg-slate-800 rounded text-white italic px-3" title="Italic">I</button>
                <button type="button" onClick={() => insertTag('u')} className="p-2 hover:bg-slate-800 rounded text-white underline px-3" title="Underline">U</button>
                <div className="w-px h-6 bg-slate-800 mx-1"></div>
                <button type="button" onClick={() => insertTag('h3')} className="p-2 hover:bg-slate-800 rounded text-white font-bold" title="Heading">H3</button>
                <button type="button" onClick={() => insertTag('p')} className="p-2 hover:bg-slate-800 rounded text-white" title="Paragraph">P</button>
                <button type="button" onClick={() => insertTag('br')} className="p-2 hover:bg-slate-800 rounded text-white" title="Line Break">BR</button>
                <div className="w-px h-6 bg-slate-800 mx-1"></div>
                <button type="button" onClick={() => insertTag('img')} className="p-2 hover:bg-slate-800 rounded text-white" title="Insert Image">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </button>
                <button type="button" onClick={() => insertTag('a')} className="p-2 hover:bg-slate-800 rounded text-white" title="Insert Link">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                  </svg>
                </button>
              </div>
              <textarea
                ref={textareaRef}
                className="w-full h-96 px-4 py-3 rounded-b-xl bg-slate-950 border border-slate-800 text-white font-mono text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all"
                placeholder="Write your blog content here (HTML supported)..."
                value={content}
                onChange={(e) => setContent(e.target.value)}
                required
              />
            </div>

            {message.text && (
              <div className={`p-4 rounded-xl text-sm font-medium border ${
                message.type === 'success' 
                  ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' 
                  : 'bg-red-500/10 text-red-400 border-red-500/20'
              }`}>
                {message.text}
              </div>
            )}

            <div className="flex gap-4">
              <button
                type="button"
                onClick={handleCancel}
                className="flex-1 py-4 px-6 rounded-xl font-bold text-slate-300 bg-slate-800 hover:bg-slate-700 transition-all"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                className={`flex-[2] py-4 px-6 rounded-xl font-bold text-white transition-all shadow-lg hover:shadow-blue-500/20 active:scale-[0.98] ${
                  saving 
                    ? 'bg-slate-700 cursor-not-allowed' 
                    : 'bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500'
                }`}
              >
                {saving ? 'Saving...' : editingBlog.id ? 'Update Post' : 'Publish Post'}
              </button>
            </div>
          </div>
        </form>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {blogs.length === 0 ? (
            <div className="bg-slate-900 border border-slate-800 rounded-2xl p-12 text-center space-y-4">
              <div className="w-16 h-16 bg-slate-800 rounded-full flex items-center justify-center mx-auto text-slate-500">
                <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
                </svg>
              </div>
              <p className="text-slate-400">No blog posts found. Create your first post!</p>
            </div>
          ) : (
            blogs.map((blog) => (
              <div key={blog.id} className="bg-slate-900 border border-slate-800 rounded-2xl p-6 flex flex-col md:flex-row md:items-center justify-between gap-6 group hover:border-blue-500/30 transition-all">
                <div className="flex items-center gap-6">
                  {blog.featuredImage && (
                    <img src={blog.featuredImage} alt={blog.title} className="w-16 h-16 rounded-lg object-cover bg-slate-800" />
                  )}
                  <div className="space-y-1">
                    <h3 className="text-xl font-bold text-white group-hover:text-blue-400 transition-colors uppercase">{blog.title}</h3>
                    <div className="flex items-center gap-4 text-sm text-slate-500">
                      <span>By {blog.author || 'Admin'}</span>
                      <span>•</span>
                      <span>{blog.createdAt?.toDate ? new Date(blog.createdAt.toDate()).toLocaleDateString() : 'Just now'}</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => handleEdit(blog)}
                    className="p-3 bg-slate-800 hover:bg-blue-600/20 hover:text-blue-400 text-slate-400 rounded-xl transition-all"
                    title="Edit Post"
                  >
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                  </button>
                  <button
                    onClick={() => handleDelete(blog.id)}
                    className="p-3 bg-slate-800 hover:bg-red-600/20 hover:text-red-400 text-slate-400 rounded-xl transition-all"
                    title="Delete Post"
                  >
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
